import { useEffect, useRef } from "preact/hooks";
import { audio } from "../audio/engine.ts";
import { playSelectLine } from "../audio/voices.ts";
import { Game } from "../game/game.ts";
import { identity, navigate } from "./App.tsx";
import { Chat } from "./Chat.tsx";
import { HeroSelect } from "./HeroSelect.tsx";
import { Hud } from "./Hud.tsx";
import { Lobby } from "./Lobby.tsx";
import { MatchEnd } from "./MatchEnd.tsx";
import { Scoreboard } from "./Scoreboard.tsx";
import { toast, ui } from "./store.ts";

let game: Game | undefined;
export const currentGame = () => game;

export function GameView({ room }: { room: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const tags = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let dead = false;
    ui.overlay.value = "menu";
    ui.status.value = "connecting";
    Game.start(canvas.current!, tags.current!, room, identity.name, identity.token)
      .then((g) => {
        if (dead) g.dispose();
        else {
          game = g;
          Object.assign(window as object, { __game: g, __audio: audio });
        }
      })
      .catch((e) => {
        console.error(e);
        ui.status.value = "error";
        ui.statusText.value = String(e?.message ?? e);
      });

    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.tagName === "INPUT";
      if (e.code === "Tab") {
        e.preventDefault();
        if (e.type === "keydown" && !e.repeat) ui.scoreboard.value = true;
        if (e.type === "keyup") ui.scoreboard.value = false;
        return;
      }
      if (e.type !== "keydown" || typing) return;
      if (e.code === "Escape") {
        // Pointer lock release already consumes the first Escape in most browsers.
        if (ui.overlay.value !== "none") closeOverlay();
        else ui.overlay.value = "menu";
      } else if (e.code === "KeyH" || e.code === "KeyB") {
        ui.overlay.value = ui.overlay.value === "heroes" ? "none" : "heroes";
        if (ui.overlay.value === "heroes") game?.input.unlock();
        else game?.input.lock();
      } else if (e.code === "Enter" && ui.overlay.value === "none") {
        e.preventDefault();
        ui.chatOpen.value = true;
      } else if (e.code === "F4") {
        e.preventDefault();
        ui.hitboxes.value = !ui.hitboxes.value;
      } else if (e.code === "F3" || e.code === "Backquote") {
        e.preventDefault();
        ui.netGraph.value = !ui.netGraph.value;
      }
    };
    addEventListener("keydown", onKey);
    addEventListener("keyup", onKey);
    const onLock = () => {
      if (!document.pointerLockElement && ui.overlay.value === "none" && !ui.chatOpen.value) ui.overlay.value = "menu";
    };
    document.addEventListener("pointerlockchange", onLock);
    return () => {
      dead = true;
      removeEventListener("keydown", onKey);
      removeEventListener("keyup", onKey);
      document.removeEventListener("pointerlockchange", onLock);
      game?.dispose();
      game = undefined;
    };
  }, [room]);

  const status = ui.status.value;
  return (
    <>
      <canvas
        ref={canvas}
        class="world"
        tabIndex={0}
        onClick={() => {
          audio.resume();
          if (ui.overlay.value === "none") game?.input.lock();
        }}
      />
      <div ref={tags} class="tags" />
      {status === "connected" && <Hud />}
      <Chat onSend={(text, team) => game?.send({ t: "chat", text, team })} />
      {ui.scoreboard.value && <Scoreboard />}
      {ui.overlay.value === "menu" && status === "connected" && <Lobby onPlay={play} onLeave={() => navigate("/")} />}
      {ui.overlay.value === "heroes" && <HeroSelect onClose={closeOverlay} onSelect={playSelectLine} />}
      {ui.matchEnd.value && <MatchEnd />}
      {status !== "connected" && (
        <div class="connecting" role="status">
          <div>
            <h1 class="title">
              <span>Rival</span>
              <span>watch</span>
            </h1>
            <p>{status === "disconnected" || status === "error" ? ui.statusText.value || "Disconnected" : ui.statusText.value || "Connecting…"}</p>
            {(status === "disconnected" || status === "error") && (
              <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", marginTop: "1rem" }}>
                <button class="btn brass" onClick={() => location.reload()}>
                  <span>Reconnect</span>
                </button>
                <button class="btn ghost" onClick={() => navigate("/")}>
                  <span>Leave</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function play() {
  ui.overlay.value = "none";
  audio.resume();
  game?.input.lock();
  setTimeout(() => {
    if (!game?.input.locked) toast("Click the game to capture your mouse");
  }, 400);
}

export function closeOverlay() {
  ui.overlay.value = "none";
  game?.input.lock();
}
